import { reserveCbtSeat, reserveCbtSeatForCombo } from '@api/fetchers/cbt';
import { CBT_RESERVE_FLOW } from '@cont/SeeAll/constants';
import { segment } from '@actions/analytics';
import {
  CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
  CHECKOUT_STARTED
} from '@cont/Checkout/constants/segment';
import {
  getCbtUserDetailsSubmittedEventProps,
  getSkuBasedEventProps,
  getTestSeriesReferralInStorage,
  reserveCbtSeatWithFeedback
} from '@cont/SeeAll/utils';
import { evtNameCategoryMap } from '@constants/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSnackbar } from '@stores/snackbar';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getEmailId, getMeUid } from '@api/selectors/auth';
import useSkuDetails from './useSkuDetails';
import useTestSeriesInfo from './useTestSeriesInfo';

export const useStoreCbtCheckout = ({
  programmeUid,
  goalUid,
  goalSlug = '-',
  skuUid,
  referralCode,
  getSegmentProps,
  getUserDetailsSubmittedProps: getUserDetailsSubmittedPropsOverride,
  programmeComposition,
  goalSegmentData = {},
  entityProps = {},
  reserveFlow = CBT_RESERVE_FLOW.STORE
}) => {
  const { open: showSnackbar } = useSnackbar();
  const userEmail = useMyInfo(getEmailId);
  const userUid = useMyInfo(getMeUid);

  const getUserDetailsSubmittedProps =
    getUserDetailsSubmittedPropsOverride ??
    ((venueSelection) =>
      getCbtUserDetailsSubmittedEventProps({
        venueSelection,
        goalSegmentData,
        entityProps,
        programmeComposition
      }));

  const proceedToCheckout = async (venueSelection = null) => {
    let learnerDetailsUid = null;

    if (venueSelection && programmeUid) {
      const { fullName, centreUid } = venueSelection;
      const reserveCbtSeatApi =
        reserveFlow === CBT_RESERVE_FLOW.COMBO
          ? reserveCbtSeatForCombo
          : reserveCbtSeat;
      learnerDetailsUid = await reserveCbtSeatWithFeedback({
        showSnackbar,
        reserveApiCall: () =>
          reserveCbtSeatApi(programmeUid, {
            centreUid,
            userId: userUid,
            fullName: fullName.trim(),
            email: userEmail
          })
      });
      if (!learnerDetailsUid) return;
    }

    const queryParams = new URLSearchParams();
    if (referralCode) {
      queryParams.set('referral_code', referralCode);
    }
    if (learnerDetailsUid) {
      queryParams.set('learner_details_uid', learnerDetailsUid);
    }
    const queryStr = queryParams.toString() ? `?${queryParams.toString()}` : '';

    pushRoute(
      `/goal/[goalSlug]/[goalUID]/subscribe/${skuUid}`,
      `/goal/${goalSlug}/${goalUid}/subscribe/${skuUid}${queryStr}`
    );
    segment.track(
      CHECKOUT_STARTED,
      getSegmentProps(venueSelection),
      evtNameCategoryMap.BOTH
    );
    segment.track(
      CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
      getSegmentProps(venueSelection),
      evtNameCategoryMap.BOTH
    );
  };

  return { proceedToCheckout, getUserDetailsSubmittedProps };
};

const useCbtVenueCheckout = () => {
  const { query } = useRouter();
  const { data } = useTestSeriesInfo();
  const { goal = {} } = data;
  const { availableSku } = useSkuDetails(goal.uid, query.courseUID);
  const goalSegmentData = useGoalEventProps({ goalUID: goal.uid });

  return useStoreCbtCheckout({
    programmeUid: query.courseUID,
    goalUid: goal.uid,
    goalSlug: goal.slug,
    skuUid: availableSku?.uid,
    referralCode: getTestSeriesReferralInStorage(query.courseUID),
    goalSegmentData,
    entityProps: getSkuBasedEventProps(availableSku, data),
    programmeComposition: data?.programmeComposition,
    getSegmentProps: (venueSelection) => ({
      ...goalSegmentData,
      ...getSkuBasedEventProps(availableSku, data),
      ...(venueSelection && {
        selected_state_name: venueSelection.state,
        selected_city_name: venueSelection.city,
        selected_test_centre_name: venueSelection.centreName,
        learner_legal_name: venueSelection.fullName.trim()
      })
    })
  });
};

export default useCbtVenueCheckout;
